import React, { useEffect, useState } from "react";
import "./App.css";
import "./campaign.css";
import "./editorial.css";
import "./partners.css";
import "./system-map.css";
import "./trust-center.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { hydrateImageOverrides } from "./lib/images";

import Nav from "./components/Nav";
import Footer from "./components/Footer";
import DemoDialog from "./components/DemoDialog";
import { RouteMetadata } from "./components/RouteMetadata";
import { FloatingHeliosChat } from "./components/chat/FloatingHeliosChat";
import { SiteUpdateNotice } from "./components/SiteUpdateNotice";

import HomePage from "./pages/HomePage";
import ExperiencePage from "./pages/ExperiencePage";
import PartnersPage from "./pages/PartnersPage";
import SystemIntegratorsPage from "./pages/SystemIntegratorsPage";
import EmbeddedWorldModelsPage from "./pages/EmbeddedWorldModelsPage";
import ProductPage from "./pages/ProductPage";
import BrainPage from "./pages/BrainPage";
import FactoryPage from "./pages/FactoryPage";
import FrameworkPage from "./pages/FrameworkPage";
import HeliosPage from "./pages/HeliosPage";
import AdminPhotosPage from "./pages/AdminPhotosPage";
import ResearchPage from "./pages/ResearchPage";
import IndustriesPage from "./pages/IndustriesPage";
import IndustryPage from "./pages/IndustryPage";
import AboutPage from "./pages/AboutPage";
import CompanyPage from "./pages/CompanyPage";
import CareersPage from "./pages/CareersPage";
import PaperDetailPage from "./pages/PaperDetailPage";
import EuropePage from "./pages/EuropePage";
import TrustPage from "./pages/TrustPage";
import TrustCenterPage from "./pages/TrustCenterPage";
import ImprintPage from "./pages/ImprintPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import "./mobile.css";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const frame = requestAnimationFrame(() => document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ behavior: "smooth", block: "start" }));
      return () => cancelAnimationFrame(frame);
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function RedirectIndustry() {
  const { slug } = useParams();
  return <Navigate to={`/industries/${slug}`} replace />;
}

function Shell() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const openDemo = () => setDemoOpen(true);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const [, force] = useState(0);

  useEffect(() => {
    hydrateImageOverrides().then(() => force((n) => n + 1));
  }, []);

  return (
    <div className="App" style={{ background: "var(--bg-primary)" }}>
      {!isAdmin && <Nav onDemo={openDemo} onMobileMenuChange={setMobileMenuOpen} theme="dark" />}
      <ScrollToTop />
      {!isAdmin && <RouteMetadata />}
      <Routes>
        <Route path="/" element={<HomePage onDemo={openDemo} />} />
        <Route path="/product" element={<ProductPage onDemo={openDemo} />} />
        <Route path="/experience" element={<ExperiencePage onDemo={openDemo} />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/partners/system-integrators" element={<SystemIntegratorsPage />} />
        <Route path="/partners/embedded-world-models" element={<EmbeddedWorldModelsPage />} />
        <Route path="/product/helios" element={<HeliosPage onDemo={openDemo} />} />
        <Route path="/product/llm" element={<Navigate to="/product/helios" replace />} />
        <Route path="/product/slm" element={<Navigate to="/product/helios" replace />} />
        <Route path="/product/brain" element={<BrainPage onDemo={openDemo} />} />
        <Route path="/product/factory" element={<FactoryPage onDemo={openDemo} />} />
        <Route path="/framework" element={<FrameworkPage onDemo={openDemo} />} />
        <Route path="/admin/photos" element={<AdminPhotosPage />} />
        <Route path="/research" element={<ResearchPage />} />
        <Route path="/industries" element={<IndustriesPage onDemo={openDemo} />} />
        <Route path="/industries/:slug" element={<IndustryPage onDemo={openDemo} />} />
        {/* Legacy redirects from /use-cases to /industries */}
        <Route path="/use-cases" element={<Navigate to="/industries" replace />} />
        <Route path="/use-cases/:slug" element={<RedirectIndustry />} />
        <Route path="/about" element={<AboutPage onDemo={openDemo} />} />
        <Route path="/company" element={<CompanyPage onDemo={openDemo} />} />
        <Route path="/careers" element={<CareersPage />} />
        {/* Papers. Kept as detail route; index is the research feed */}
        <Route path="/papers" element={<Navigate to="/research?tab=papers" replace />} />
        <Route path="/papers/:slug" element={<PaperDetailPage />} />
        {/* Lab / Notes. Merged into the research feed */}
        <Route path="/lab" element={<Navigate to="/research?tab=notes" replace />} />
        <Route path="/notes" element={<Navigate to="/research?tab=questions" replace />} />
        <Route path="/europe" element={<EuropePage onDemo={openDemo} />} />
        <Route path="/trust" element={<TrustPage onDemo={openDemo} />} />
        <Route path="/trust-center" element={<TrustCenterPage onDemo={openDemo} />} />
        <Route path="/imprint" element={<ImprintPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="*" element={<HomePage onDemo={openDemo} />} />
      </Routes>
      {!isAdmin && <Footer theme="dark" />}
      <DemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
      {!isAdmin && <FloatingHeliosChat hidden={demoOpen || mobileMenuOpen} onOpenChange={setChatOpen} />}
      {!isAdmin && <SiteUpdateNotice hidden={demoOpen || mobileMenuOpen || chatOpen} />}
      <Toaster
        theme="dark"
        position="bottom-left"
        toastOptions={{
          style: {
            background: "var(--bg-secondary)",
            border: "1px solid var(--border-hairline)",
            color: "var(--text-primary)",
            fontFamily: "Satoshi, sans-serif",
            borderRadius: 6,
          },
        }}
      />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}
